import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import AuthModal from './AuthModal';
import { useLang } from '../../../i18n/LanguageContext';

interface NavbarProps {
  scrolled?: boolean;
  onOpenAuth?: (tab: 'signin' | 'signup') => void;
}

interface CurrentUser {
  name: string;
  email: string;
}

export default function Navbar({ scrolled: scrolledProp, onOpenAuth }: NavbarProps) {
  const [scrolled, setScrolled] = useState(scrolledProp ?? false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'signin' | 'signup'>('signin');
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { lang, setLang, t } = useLang();

  // 初始化时读取已登录用户
  useEffect(() => {
    const stored = localStorage.getItem('mystic_current_user');
    if (stored) {
      try { setCurrentUser(JSON.parse(stored)); } catch { /* ignore */ }
    }
  }, []);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (scrolledProp !== undefined) return;
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolledProp]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleAuthSuccess = (user: CurrentUser) => {
    setCurrentUser(user);
    setAuthOpen(false);
  };

  const handleSignOut = () => {
    localStorage.removeItem('mystic_current_user');
    setCurrentUser(null);
    setDropdownOpen(false);
    setMenuOpen(false);
  };

  const openSignIn = () => {
    setMenuOpen(false);
    if (onOpenAuth) { onOpenAuth('signin'); return; }
    setAuthTab('signin'); setAuthOpen(true);
  };
  const openSignUp = () => {
    setMenuOpen(false);
    if (onOpenAuth) { onOpenAuth('signup'); return; }
    setAuthTab('signup'); setAuthOpen(true);
  };

  const navLinks = [
    { id: 'home', path: '/', label: t('Home', '主页') },
    { id: 'courses', path: '/courses', label: t('Courses', '课程') },
    { id: 'certification', path: '/membership', label: t('Student Discounts', '学员优惠') },
    { id: 'community-faq', path: '/academy', label: t('About Us', '关于我们') },
  ];

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 取名字首字母作为头像
  const getInitials = (name: string) => name.trim().charAt(0).toUpperCase();

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#2d1b4e]/90 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        {/* 语言切换条 */}
        <div className="w-full border-b border-[#d4af37]/10 bg-[#1a0b2e]/60 backdrop-blur-sm">
          <div className="flex justify-end items-center px-4 md:px-8 py-1.5 gap-1">
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                lang === 'en' ? 'bg-[#d4af37] text-[#1a0b2e]' : 'text-[#c8b6ff] hover:text-[#d4af37]'
              }`}
            >
              EN
            </button>
            <span className="text-[#d4af37]/30 text-xs">|</span>
            <button
              onClick={() => setLang('zh')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                lang === 'zh' ? 'bg-[#d4af37] text-[#1a0b2e]' : 'text-[#c8b6ff] hover:text-[#d4af37]'
              }`}
            >
              中文
            </button>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-2">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img
                src="https://static.readdy.ai/image/f4ea41724fdff553620ff2345610d616/46f66eaf14331da7e5a01089d8e059d5.png"
                alt="Logo"
                className="w-8 h-8 md:w-10 md:h-10 object-contain"
              />
              <span className="text-base md:text-xl font-serif text-[#d4af37]">SSD Mystic Academy</span>
            </div>

            {/* 桌面端导航链接 */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.path}
                  className="text-[#d4af37] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/tarot"
                className="text-[#d4af37] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center space-x-1"
              >
                <i className="ri-sparkling-2-line"></i>
                <span>{t('Talent Test', '天赋测试')}</span>
              </Link>
              <Link
                to="/certificate"
                className="text-[#d4af37] hover:text-white transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center space-x-1"
              >
                <i className="ri-award-line"></i>
                <span>{t('Verification', '证书查询')}</span>
              </Link>
            </div>

            {/* 桌面端：已登录显示用户信息，未登录显示按钮 */}
            <div className="hidden lg:flex items-center space-x-4">
              {currentUser ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-1.5 rounded-full border border-[#d4af37]/40 hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#f4d03f] flex items-center justify-center text-[#1a0b2e] font-bold text-sm">
                      {getInitials(currentUser.name)}
                    </div>
                    <span className="text-[#d4af37] text-sm font-medium max-w-[100px] truncate">{currentUser.name}</span>
                    <i className={`ri-arrow-down-s-line text-[#d4af37] text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* 下拉菜单 */}
                  {dropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-[#1a0d35] border border-[#d4af37]/30 rounded-xl shadow-2xl overflow-hidden z-50">
                      <div className="px-4 py-3 border-b border-[#d4af37]/20">
                        <p className="text-white text-sm font-semibold truncate">{currentUser.name}</p>
                        <p className="text-[#c8b6ff] text-xs truncate mt-0.5">{currentUser.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center space-x-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors cursor-pointer"
                        >
                          <i className="ri-logout-box-r-line"></i>
                          <span>{t('Sign Out', '退出登录')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={openSignIn}
                    className="px-6 py-2 border border-[#d4af37] text-[#d4af37] rounded-full hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {t('Sign In', '登录')}
                  </button>
                  <button
                    onClick={openSignUp}
                    className="px-6 py-2 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-white rounded-full hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center space-x-2"
                  >
                    <span>{t('Enroll Now', '立即报名')}</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </>
              )}
            </div>

            {/* 移动端汉堡按钮 */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#d4af37] hover:text-white transition-colors duration-300 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* 移动端抽屉菜单遮罩 */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMenuOpen(false)}
      />

      {/* 移动端抽屉菜单 */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 bg-[#1a0b2e] border-l border-[#d4af37]/20 shadow-2xl transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* 抽屉顶部：Logo + 品牌名，与桌面端一致 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#d4af37]/20">
          <div className="flex items-center space-x-2">
            <img
              src="https://static.readdy.ai/image/f4ea41724fdff553620ff2345610d616/46f66eaf14331da7e5a01089d8e059d5.png"
              alt="Logo"
              className="w-8 h-8 object-contain"
            />
            <span className="text-sm font-serif text-[#d4af37]">SSD Mystic Academy</span>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-8 h-8 flex items-center justify-center text-[#d4af37] hover:text-white cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* 移动端已登录用户信息 */}
        {currentUser && (
          <div className="px-5 py-3 border-b border-[#d4af37]/20 flex items-center space-x-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#d4af37] to-[#f4d03f] flex items-center justify-center text-[#1a0b2e] font-bold text-sm flex-shrink-0">
              {getInitials(currentUser.name)}
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate">{currentUser.name}</p>
              <p className="text-[#c8b6ff] text-xs truncate">{currentUser.email}</p>
            </div>
          </div>
        )}

        {/* 导航链接列表：与桌面端完全一致，同样的链接同样的顺序 */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <nav className="space-y-1">
            {[
              { id: 'home',        path: '/',            label: t('Home', '主页'),             icon: 'ri-home-5-line' },
              { id: 'courses',     path: '/courses',     label: t('Courses', '课程'),           icon: 'ri-book-2-line' },
              { id: 'membership',  path: '/membership',  label: t('Student Discounts', '学员优惠'), icon: 'ri-price-tag-3-line' },
              { id: 'academy',     path: '/academy',     label: t('About Us', '关于我们'),      icon: 'ri-information-line' },
              { id: 'tarot',       path: '/tarot',       label: t('Talent Test', '天赋测试'),   icon: 'ri-sparkling-2-line' },
              { id: 'certificate', path: '/certificate', label: t('Verification', '证书查询'),  icon: 'ri-award-line' },
            ].map((link) => (
              <Link
                key={link.id}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-xl text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-white transition-all duration-200 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${link.icon} text-base`}></i>
                </div>
                <span className="text-sm font-medium">{link.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* 底部按钮：与桌面端一致 */}
        <div className="px-4 py-4 border-t border-[#d4af37]/20 space-y-3">
          {currentUser ? (
            <button
              onClick={handleSignOut}
              className="w-full py-2.5 border border-red-400/50 text-red-400 rounded-full hover:bg-red-500/10 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium flex items-center justify-center space-x-2"
            >
              <i className="ri-logout-box-r-line"></i>
              <span>{t('Sign Out', '退出登录')}</span>
            </button>
          ) : (
            <>
              <button
                onClick={openSignIn}
                className="w-full py-2.5 border border-[#d4af37] text-[#d4af37] rounded-full hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                {t('Sign In', '登录')}
              </button>
              <button
                onClick={openSignUp}
                className="w-full py-2.5 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] rounded-full hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-bold flex items-center justify-center space-x-2"
              >
                <span>{t('Enroll Now', '立即报名')}</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            </>
          )}
        </div>

        {/* 语言切换：与顶部语言条一致 */}
        <div className="px-4 pb-5 flex items-center justify-center gap-2">
          <button
            onClick={() => setLang('en')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              lang === 'en' ? 'bg-[#d4af37] text-[#1a0b2e]' : 'text-[#c8b6ff] hover:text-[#d4af37]'
            }`}
          >
            EN
          </button>
          <span className="text-[#d4af37]/30 text-xs">|</span>
          <button
            onClick={() => setLang('zh')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              lang === 'zh' ? 'bg-[#d4af37] text-[#1a0b2e]' : 'text-[#c8b6ff] hover:text-[#d4af37]'
            }`}
          >
            中文
          </button>
        </div>
      </div>

      {!onOpenAuth && (
        <AuthModal
          isOpen={authOpen}
          defaultTab={authTab}
          onClose={() => setAuthOpen(false)}
          onSuccess={handleAuthSuccess}
        />
      )}
    </>
  );
}
